/**
 * Durable queue for proctoring disqualification reports.
 *
 * POST /proctoring/disqualify used to be fire-and-forget: when the request failed
 * (the Proctoring feature flag stranded off => 403, a dropped connection, or the
 * student closing the tab right after disqualification) the report was lost with
 * nothing but a console warning — the student still saw "Disqualified" while
 * faculty received nothing.
 *
 * Reports that cannot be delivered are kept in localStorage and replayed on the
 * next dashboard load.
 */
import api from '../api';

const STORAGE_KEY = 'pop_pending_proctor_reports';
const MAX_QUEUED = 25;

/** Send one disqualification report to the backend. */
export const postProctorReport = (report) => api.post('/proctoring/disqualify', report);

export const getQueuedProctorReports = () => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (err) {
    console.warn('Could not read queued proctoring reports:', err);
    return [];
  }
};

const writeQueue = (reports) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(reports));
  } catch (err) {
    // Snapshots are base64 images, so the quota can be hit; keep the newest
    // reports rather than losing the queue entirely.
    console.warn('Could not persist queued proctoring reports:', err);
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(reports.slice(-5)));
    } catch (fallbackErr) {
      console.error('Giving up persisting proctoring reports:', fallbackErr);
    }
  }
};

/** Persist a report that could not be delivered. Returns the new queue length. */
export const queueProctorReport = (report) => {
  const queue = [...getQueuedProctorReports(), report];
  const trimmed = queue.slice(-MAX_QUEUED);
  writeQueue(trimmed);
  return trimmed.length;
};

/**
 * Replay everything queued. A report is only dropped from the queue once the
 * backend has accepted it, so nothing is lost if a flush is interrupted.
 * Returns { sent, failed }.
 */
export const flushQueuedProctorReports = async () => {
  const queue = getQueuedProctorReports();
  if (!queue.length) return { sent: 0, failed: 0 };

  const failed = [];
  let sent = 0;
  for (const report of queue) {
    try {
      await postProctorReport(report);
      sent += 1;
    } catch (err) {
      failed.push(report);
    }
  }

  writeQueue(failed);
  if (sent) console.info(`Delivered ${sent} queued proctoring report(s).`);
  return { sent, failed: failed.length };
};
